import axios from 'axios';
import { validateAuth } from './validationUtils.js';

const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB chunks
const MAX_CONCURRENT_UPLOADS = 3;

const MEDIA_API_URL = process.env.REACT_APP_API_MEDIA_URL;

/**
 * Upload large files using signed URLs and chunked upload
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<string>} The uploaded file URL
 */
export const uploadLargeFileWithSignedUrl = async (file, onProgress = null) => {
  const token = validateAuth();
  
  try {
    console.log('🚀 Starting signed URL upload for large file:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    // Step 1: Get signed URL from backend
    console.log('📡 Requesting signed URL from backend...');
    console.log('📊 Request details:', {
      url: `${MEDIA_API_URL}/start-upload`,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type
    });
    
    const startResponse = await axios.post(
      `${MEDIA_API_URL}/start-upload`,
      {
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type
      },
      {
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      }
    );

    console.log('📡 Backend response:', startResponse.data);

    if (!startResponse.data.result) {
      throw new Error('Failed to get signed URL from backend');
    }

    const { uploadId, sasUrl } = startResponse.data.result;
    console.log('✅ Got signed URL for upload:', {
      uploadId,
      sasUrlLength: sasUrl?.length || 0
    });

    // Step 2: Upload file in chunks
    const chunks = Math.ceil(file.size / CHUNK_SIZE);
    const blockIds = [];
    let uploadedBytes = 0;

    console.log(`📦 Starting chunked upload: ${chunks} chunks of ${CHUNK_SIZE / (1024*1024)}MB each`);
    console.log('📊 Chunk details:', {
      totalChunks: chunks,
      chunkSize: CHUNK_SIZE,
      chunkSizeMB: (CHUNK_SIZE / (1024*1024)).toFixed(2) + 'MB',
      maxConcurrent: MAX_CONCURRENT_UPLOADS
    });

    // Upload chunks with concurrency control
    const uploadPromises = [];
    const chunkStartTime = Date.now();
    
    for (let i = 0; i < chunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      // Generate block ID (must be base64 encoded and same length for all blocks)
      const blockId = btoa(String(i).padStart(6, '0')).replace(/=/g, '');
      blockIds.push(blockId);


      // Limit concurrent uploads
      if (uploadPromises.length >= MAX_CONCURRENT_UPLOADS) {
        await Promise.race(uploadPromises);
      }

      const uploadPromise = uploadChunk(sasUrl, blockId, chunk)
        .then(() => {
          uploadedBytes += chunk.size;
          if (onProgress) {
            const percentage = Math.round((uploadedBytes / file.size) * 100);
            onProgress({ 
              percentage, 
              uploadedBytes, 
              totalBytes: file.size,
              chunksCompleted: Math.ceil(uploadedBytes / CHUNK_SIZE)
            });
          }
        })
        .finally(() => {
          // Remove completed promise from array
          const index = uploadPromises.indexOf(uploadPromise);
          if (index > -1) uploadPromises.splice(index, 1);
        });

      uploadPromises.push(uploadPromise);
    }

    // Wait for all chunks to complete
    await Promise.all(uploadPromises);
    
    // Step 3: Commit the upload
    
    const commitStartTime = Date.now();
    const commitResponse = await axios.post(
      `${MEDIA_API_URL}/commit-upload`,
      {
        uploadId,
        blockIds,
        mimeType: file.type
      },
      {
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      }
    );

    const commitEndTime = Date.now();
    const commitDuration = (commitEndTime - commitStartTime) / 1000;
    console.log('📡 Commit response:', commitResponse.data);
    console.log('⏱️ Commit duration:', commitDuration + 's');

    // Extract URL: prefer explicit result.url, fall back to base blob URL from sasUrl
    let finalUrl;
    if (commitResponse.data.result && commitResponse.data.result.url) {
      finalUrl = commitResponse.data.result.url;
    } else if (commitResponse.data.url) {
      finalUrl = commitResponse.data.url;
    } else {
      // Derive public blob URL by stripping the SAS token query string
      finalUrl = sasUrl.split('?')[0];
    }

    if (!finalUrl) {
      throw new Error('Failed to commit upload: no URL returned');
    }
    console.log('🎉 Large file upload completed successfully!', {
      url: finalUrl,
      totalDuration: ((commitEndTime - Date.now() + commitEndTime) / 1000) + 's'
    });

    return finalUrl;

  } catch (error) {
    console.error('❌ Error in signed URL upload:', error);
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
    }
    throw new Error(`Signed URL upload failed: ${error.message}`);
  }
};

/**
 * Upload a single chunk to Azure Blob Storage using signed URL
 */
const uploadChunk = async (sasUrl, blockId, chunk) => {
  const maxRetries = 3;
  let retries = 0;

  while (retries < maxRetries) {
    try {
      const chunkUrl = `${sasUrl}&comp=block&blockid=${blockId}`;
      
      const response = await fetch(chunkUrl, {
        method: 'PUT',
        body: chunk,
        headers: {
          'Content-Length': chunk.size.toString()
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return;
    } catch (error) {
      retries++;
      if (retries >= maxRetries) {
        throw error;
      }
      // Exponential backoff
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, retries) * 1000));
    }
  }
};

/**
 * Check if file needs signed URL upload
 */
export const needsSignedUrlUpload = (file) => {
  return file.size > 100 * 1024 * 1024; // 100MB threshold
};

export default {
  uploadLargeFileWithSignedUrl,
  needsSignedUrlUpload
};
